import { useState } from 'react';
import { Select, MenuItem, Box, Typography, Chip, SelectChangeEvent } from '@mui/material';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { en } from '../translations/en';
import { logger } from '../utils/logger';

const PERMISSION_LABELS: Record<string, string> = {
  view: en.catalogSwitcher.permission.view,
  full: en.catalogSwitcher.permission.full,
};

/**
 * The catalog the user is attempting to switch to. Passed to the
 * `onBeforeSwitch` gate.
 *
 * Field nullability is asymmetric by design:
 *
 * - `catalogAliId: string` + `ownerUserId: null` — switching to one of
 *   the current user's own catalogs (primary or add-on). Owner is the
 *   caller themselves; no separate ownerUserId is meaningful.
 * - `catalogAliId: string` + `ownerUserId: string` — switching to a
 *   delegated catalog. ownerUserId is the delegator.
 * - `catalogAliId: null` + `ownerUserId: null` — sentinel for "no
 *   target selected" (defensive only; the Select never emits this
 *   under normal use).
 *
 * The combination `catalogAliId: null` + `ownerUserId: string` is
 * never produced — it would mean "delegated to nobody," which has no
 * meaning. The type does not statically prevent it; the gate is
 * responsible for the invariant via `availableCatalogs` lookup.
 */
export interface CvCatalogSwitchTarget {
  catalogAliId: string | null;
  ownerUserId: string | null;
}

export interface CvCatalogSelectProps {
  /**
   * Called BEFORE the active catalog actually changes. Resolve true to
   * proceed, false to abort. Used by the CV edit page to confirm before
   * discarding unsaved changes. The Select's visible value is restored
   * when this returns false so the user sees no apparent state change.
   *
   * Switches via the navbar Catalog menu do NOT route through this gate
   * — that's a separate, page-leaving flow handled by `CatalogMenuList`.
   */
  onBeforeSwitch?: (target: CvCatalogSwitchTarget) => Promise<boolean>;
}

/**
 * Standalone in-page catalog switcher used on the CV view and edit pages.
 *
 * Distinct from `CatalogMenuList`: that component lives inside the navbar's
 * Catalog menu and navigates to `/my-objects` on selection. This one stays
 * on the current page (the CV) — it only flips the active catalog so the
 * CV refetches for the new owner. Hides itself when there is nothing to
 * switch between (≤1 available catalog).
 */
export const CvCatalogSelect = ({ onBeforeSwitch }: CvCatalogSelectProps = {}) => {
  const ctx = useActiveCatalog();
  // Defensive defaults — partial mocks in some unit tests don't provide
  // the full PR-4b shape. Coerce to safe empty values so the component
  // renders its "nothing to show" branch instead of throwing on .length.
  const availableCatalogs = ctx.availableCatalogs ?? [];
  const activeCatalogAliId = ctx.activeCatalogAliId ?? null;
  const isLoadingOwnedCatalogs = ctx.isLoadingOwnedCatalogs ?? false;
  const isLoadingDelegations = ctx.isLoadingDelegations ?? false;
  const switchToCatalogAliId = ctx.switchToCatalogAliId ?? (() => {});

  // PR-4c Tier 2 (migration 0032): CV is now keyed per
  // (user_id, catalog_ali_id), so own add-on catalogs each carry a
  // distinct CV. Surface them in the switcher alongside the primary
  // and any delegations. The earlier own-addon filter that lived here
  // was a pre-migration safety guard — now removed.
  const selectableCatalogs = availableCatalogs;

  // Local visible value lets us revert the Select after a cancelled
  // switch — without this, the Select would briefly snap to the new
  // option and stay there even though context never changed.
  const [pendingVisibleValue, setPendingVisibleValue] = useState<string | null>(null);

  if (selectableCatalogs.length <= 1) {
    return null;
  }

  const effectiveAli = activeCatalogAliId ?? selectableCatalogs[0]?.catalogAliId ?? '';
  const renderedValue = pendingVisibleValue ?? effectiveAli;

  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    const target = value || null;
    const row = selectableCatalogs.find((c) => c.catalogAliId === value);

    if (!onBeforeSwitch) {
      switchToCatalogAliId(target);
      return;
    }
    // Optimistically show the new value while the gate resolves, then
    // revert if rejected. Never call switchToCatalogAliId before the
    // gate resolves so the consumer's load useEffect cannot race the
    // confirmation dialog.
    setPendingVisibleValue(value);
    onBeforeSwitch({
      catalogAliId: target,
      ownerUserId: row?.ownerUserId ?? null,
    })
      .then((ok) => {
        if (ok) {
          switchToCatalogAliId(target);
        }
      })
      .catch((err) => {
        // If the consumer's gate throws, we still need to clear the
        // optimistic value or the Select stays stuck on the pending
        // option visually. Log so the failure isn't silent.
        logger.error('CvCatalogSelect: onBeforeSwitch rejected', err);
      })
      .finally(() => {
        setPendingVisibleValue(null);
      });
  };

  return (
    <Select
      value={renderedValue}
      onChange={handleChange}
      disabled={isLoadingOwnedCatalogs || isLoadingDelegations}
      size="small"
      sx={{ minWidth: 200 }}
      renderValue={(value) => {
        const row = selectableCatalogs.find((c) => c.catalogAliId === value);
        if (!row) {
          logger.warn('CvCatalogSelect: active ALI not in selectableCatalogs', {
            value,
            selectableCount: selectableCatalogs.length,
          });
          return value as string;
        }
        return `${row.label} (${row.catalogAliId})`;
      }}
    >
      {selectableCatalogs.map((row) => (
        <MenuItem key={row.catalogAliId} value={row.catalogAliId}>
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <Typography>
              {row.label} ({row.catalogAliId})
            </Typography>
            <Chip
              label={
                row.kind === 'delegated'
                  ? en.catalogSwitcher.delegatedChip
                  : en.catalogSwitcher.ownerChip
              }
              size="small"
              color={row.kind === 'delegated' ? 'default' : 'primary'}
              variant="outlined"
              sx={{ ml: 'auto' }}
            />
            {row.kind === 'delegated' && row.delegation && (
              <Chip
                label={PERMISSION_LABELS[row.delegation.permissions] || row.delegation.permissions}
                size="small"
              />
            )}
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
};
